// AdminDashboard.js

import React, { useState, useMemo, useEffect } from "react";
import "./AdminDashboard.css";
import { useDispatch, useSelector } from "react-redux";

import { getAdminSubmissionsListAction } from "../../../actions/MyCertificates/certificatesAction";
import { ADMIN_MARK_COMPLETED_RESET } from "../../../constants/MyCertificates/CertificatesConstants";
import { completeUserSubmissionAction } from "../../../actions/Admin-Panels/adminPanelActions";
import { formatIssuedDate } from "../../../utils/change_time_format";

const AdminDashboard = () => {
  const dispatch = useDispatch();
  // State from Redux
  const adminSubmissionsList = useSelector(
    (state) => state.adminSubmissionsListStore
  );
  const {
    loading,
    error,
    submissions: backendSubmissions,
    page: currentPage,
    totalPages,
    total,
  } = adminSubmissionsList;

  const completedSubmission = useSelector(
    (state) => state.completeUserSubmissionStore
  );
  const {
    loading: markLoading,
    success: markSuccess,
    error: markError,
  } = completedSubmission;

  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [page, setPage] = useState(1);
  const [limit] = useState(100);

  // Helper function to map backend status to display status
  const getStatusDisplay = (status, reviewStatus) => {
    if (status === "under_review") return "In Review";
    if (reviewStatus === "approved") return "Approved";
    if (reviewStatus === "rejected") return "Rejected";
    if (reviewStatus === "pending") return "Pending";
    return "Pending";
  };

  // Fetch submissions on component mount and when page changes
  useEffect(() => {
    dispatch(getAdminSubmissionsListAction(page, limit));
  }, [dispatch, page, limit]);

  // Refetch when mark completed is successful
  useEffect(() => {
    if (markSuccess) {
      dispatch(getAdminSubmissionsListAction(page, limit));
      dispatch({ type: ADMIN_MARK_COMPLETED_RESET });
    }
  }, [markSuccess, dispatch, page, limit]);

  // Transform backend data to match component structure
  const submissions = useMemo(() => {
    if (!backendSubmissions || backendSubmissions.length === 0) return [];

    return backendSubmissions.map((item) => ({
      id: item._id,
      name: item.user?.email || "N/A",
      certificateDetails: item.certification?.title || "N/A",
      problemStatement: item.problem?.title || "N/A",
      githubLink: item.submission?.githubUrl || "#",
      status: getStatusDisplay(item.status, item.review?.status),
      certification: item?.certification,
      user: item?.user,
    }));
  }, [backendSubmissions]);

  // Handle "Mark as Complete" Action
  const handleMarkComplete = (id, user, certificateDetails) => {
    // dispatch(markSubmissionCompletedAction(id));
    dispatch(
      completeUserSubmissionAction(
        user,
        formatIssuedDate(),
        certificateDetails?.category?.title,
        id
      )
    );
  };

  // Filter Logic
  const filteredSubmissions = useMemo(() => {
    return submissions.filter((item) => {
      const matchesName = item.name
        .toLowerCase()
        .includes(searchTerm.toLowerCase());
      const matchesStatus =
        statusFilter === "All" || item.status === statusFilter;
      return matchesName && matchesStatus;
    });
  }, [submissions, searchTerm, statusFilter]);

  // Pagination handlers
  const handleNextPage = () => {
    if (page < totalPages) {
      setPage(page + 1);
    }
  };

  const handlePrevPage = () => {
    if (page > 1) {
      setPage(page - 1);
    }
  };

  const handlePageClick = (pageNum) => {
    setPage(pageNum);
  };

  // Generate page numbers for pagination
  const getPageNumbers = () => {
    const pages = [];
    const maxPagesToShow = 5;

    if (totalPages <= maxPagesToShow) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (page <= 3) {
        for (let i = 1; i <= 4; i++) pages.push(i);
        pages.push("...");
        pages.push(totalPages);
      } else if (page >= totalPages - 2) {
        pages.push(1);
        pages.push("...");
        for (let i = totalPages - 3; i <= totalPages; i++) pages.push(i);
      } else {
        pages.push(1);
        pages.push("...");
        pages.push(page - 1);
        pages.push(page);
        pages.push(page + 1);
        pages.push("...");
        pages.push(totalPages);
      }
    }

    return pages;
  };

  return (
    <div className="adminDashboard-container">
      <header className="adminDashboard-header">
        <h1 className="adminDashboard-title">Student Submissions</h1>

        <div className="adminDashboard-controls">
          <input
            type="text"
            placeholder="Search by Name..."
            className="adminDashboard-search-input"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />

          <select
            className="adminDashboard-filter-select"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="All">All Statuses</option>
            <option value="In Review">In Review</option>
            <option value="Approved">Approved</option>
            <option value="Rejected">Rejected</option>
            <option value="Pending">Pending</option>
          </select>
        </div>
      </header>

      {/* Loading and Error States */}
      {loading && (
        <div className="adminDashboard-loading">Loading submissions...</div>
      )}
      {error && <div className="adminDashboard-error">Error: {error}</div>}
      {markError && (
        <div className="adminDashboard-error">
          Error marking complete: {markError}
        </div>
      )}

      {/* Submissions Table */}
      {!loading && (
        <>
          <div className="adminDashboard-table-wrapper">
            <table className="adminDashboard-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Certificate Details</th>
                  <th>Problem Statement</th>
                  <th>GitHub Link</th>
                  <th>Status</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {filteredSubmissions.length > 0 ? (
                  filteredSubmissions.map((row) => (
                    <tr key={row?.id}>
                      <td data-label="Name">{row?.name}</td>
                      <td data-label="Certificate Details">
                        {row?.certificateDetails}
                      </td>
                      <td data-label="Problem Statement">
                        <div className="adminDashboard-text-limit">
                          {row?.problemStatement}
                        </div>
                      </td>
                      <td data-label="GitHub Link">
                        <a
                          href={row?.githubLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="adminDashboard-link"
                        >
                          {row?.githubLink}
                        </a>
                      </td>
                      <td data-label="Status">
                        <span
                          className={`adminDashboard-status-badge ${row?.status
                            ?.toLowerCase()
                            ?.replace(" ", "-")}`}
                        >
                          {row?.status}
                        </span>
                      </td>
                      <td data-label="Action">
                        {row?.status === "In Review" && (
                          <button
                            className="adminDashboard-complete-btn"
                            onClick={() =>
                              handleMarkComplete(
                                row?.id,
                                row?.user,
                                row?.certification
                              )
                            }
                            disabled={markLoading}
                          >
                            {markLoading ? "Processing..." : "Mark Complete"}
                          </button>
                        )}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="6" className="adminDashboard-empty-state">
                      No submissions found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination Controls */}
          {totalPages > 1 && (
            <div className="adminDashboard-pagination">
              <button
                className="adminDashboard-page-btn"
                onClick={handlePrevPage}
                disabled={page <= 1}
              >
                Previous
              </button>

              {getPageNumbers().map((pageNum, index) =>
                pageNum === "..." ? (
                  <span
                    key={`ellipsis-${index}`}
                    className="adminDashboard-ellipsis"
                  >
                    ...
                  </span>
                ) : (
                  <button
                    key={pageNum}
                    className={`adminDashboard-page-btn ${
                      page === pageNum ? "active" : ""
                    }`}
                    onClick={() => handlePageClick(pageNum)}
                  >
                    {pageNum}
                  </button>
                )
              )}

              <button
                className="adminDashboard-page-btn"
                onClick={handleNextPage}
                disabled={page >= totalPages}
              >
                Next
              </button>

              <span className="adminDashboard-page-info">
                Page {page} of {totalPages} ({total} total submissions)
              </span>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default AdminDashboard;
